<template>
  <OModal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.service.actions.addResource.title'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full py-4 px-4 flex flex-col"
  >
    <div v-if="serviceResource" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('components.service.actions.addResource.title') }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
          {{ $t('components.service.actions.addResource.info') }}
        </p>
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <OScroll class="py-4 px-8 h-full">
          <OForm ref="form" class="h-full">
            <div class="flex space-x-4">
              <OFieldGroup v-slot="{ status }" rules="required" class="w-full" :label="$t('general.resource')" :info="$t('components.service.actions.addResource.resourceInfo')">
                <ResourceSelect v-model="resourceModel" :status="status" class="w-full shadow-sm" />
              </OFieldGroup>
            </div>

            <p v-if="!showCustomName" class="text-right mt-1 block px-1">
              <a class="text-blue-600 text-sm hover:text-blue-700 cursor-pointer" @click="showCustomName = true">{{ $t('components.service.actions.addResource.useCustomLabel') }}</a>
            </p>

            <div v-if="showCustomName" class="flex space-x-4 mb-6 mt-6">
              <OFieldGroup v-slot="{ status }" rules="required" class="w-full" :label="$t('general.label')" :info="$t('components.service.actions.addResource.labelInfo')">
                <TextField v-model="serviceResource.name" :status="status" class="w-full shadow-sm" />
              </OFieldGroup>
            </div>

            <div class="flex space-x-4 mt-6">
              <OFieldGroup class="w-full" :label="$t('general.default')" :info="$t('components.service.actions.addResource.defaultInfo')">
                <SegmentField v-model="serviceResource.defaultSelection" :options="defaultSelectionOptions" />
              </OFieldGroup>
            </div>

            <div v-if="isResourceType && isServiceCustomerTypeGroup" class="flex space-x-4 mt-6">
              <OFieldGroup class="w-full" :label="$t('components.service.actions.addResource.assignment')" :info="$t('components.service.actions.addResource.assignmentInfo')">
                <SegmentField v-model="serviceResource.assignment" :options="assignmentOptions" />
              </OFieldGroup>
            </div>

            <div v-if="isResourceType" class="flex space-x-4 mt-6">
              <OFieldGroup v-slot="{ status }" rules="required" class="w-full" :label="$t('general.quantity')" :info="$t('components.service.actions.addResource.quantityInfo')">
                <NumberField v-model="serviceResource.quantity" :status="status" class="w-full shadow-sm" />
              </OFieldGroup>
            </div>

            <div class="flex space-x-4 mt-6">
              <OFieldGroup
                v-slot="{ status }"
                col
                reverse
                class="w-full"
                :label="$t('general.customerVisible')"
                :info="$t('components.service.actions.addResource.customerVisibleInfo')"
              >
                <ToggleField v-model="serviceResource.customerVisible" :status="status" class="w-full shadow-sm" />
              </OFieldGroup>
            </div>
          </OForm>
        </OScroll>
      </div>
    </div>
  </OModal>
</template>

<script setup lang="ts">
import ResourceSelect from '@/components/service/elements/Resource.vue'

import {
  TextField,
  NumberField,
  SegmentField,
  ToggleField,
} from '@/components/fields'

import type { Service } from '~/_api/models/service'

import { 
  ServiceResource, 
  ServiceResourceType,
  ServiceResourceAssignment,
  ServiceResourceDefaultSelection
} from '~/_api/models/serviceResource'

interface Props {
  service: Service
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const emit = defineEmits<Emits>()
const { $i18n, $notification, $api } = useNuxtApp()

const modal = ref()
const form = ref()
const serviceResource = ref<ServiceResource | null>(null)
const showCustomName = ref(false)

const resourceModel = computed({
  get: () => {
    const sr = serviceResource.value
    return sr?.resource || sr?.resourceType
  },
  set: (value: any) => {
    if (!serviceResource.value) return
    
    // Check if value is an Employee (has isEmployeeType property from the model)
    if (value && typeof value === 'object' && 'isEmployeeType' in value) {
      serviceResource.value.resource = value
      serviceResource.value.resourceType = undefined
      serviceResource.value.type = ServiceResourceType.resource
    } else {
      serviceResource.value.resource = undefined
      serviceResource.value.resourceType = value
      serviceResource.value.type = ServiceResourceType.resourceType
    }
  }
})

const isDisabled = computed(() => {
  return false
})

const isResource = computed(() => {
  return serviceResource.value?.type === ServiceResourceType.resource
})

const isResourceType = computed(() => {
  return serviceResource.value?.type === ServiceResourceType.resourceType
})

const isEmployee = computed(() => {
  return resourceModel.value?.isEmployeeType
})

const isServiceCustomerTypeGroup = computed(() => {
  return props.service.isServiceCustomerTypeGroup
})

const defaultSelectionOptions = computed(() => [
  { label: $i18n.t('components.service.actions.addResource.assignAutomatically'), value: ServiceResourceDefaultSelection.firstAvailable },
  { label: $i18n.t('components.service.actions.addResource.assignManually'), value: ServiceResourceDefaultSelection.none },
])

const assignmentOptions = computed(() => [
  { label: $i18n.t('general.customer'), value: ServiceResourceAssignment.customer },
  { label: $i18n.t('general.class'), value: ServiceResourceAssignment.reservation },
])

watch(resourceModel, (newResource) => {
  if (newResource && serviceResource.value && !serviceResource.value.name) {
    serviceResource.value.name = newResource.name || newResource.displayName || ''
  }

  if (isEmployee.value) {
    showCustomName.value = true
  } else {
    showCustomName.value = false
  }
})

const open = () => {
  setDefaults()
  modal.value.open()
}

const validate = () => {
  return form.value.validate()
}

const setDefaults = () => {
  const data = {
    type: ServiceResourceType.resource,
    resourceType: null,
    name: '',
    quantity: 1,
    defaultSelection: ServiceResourceDefaultSelection.firstAvailable,
    resource: null,
    assignment: ServiceResourceAssignment.customer,
    customerVisible: true,
  }

  serviceResource.value = new ServiceResource(data)
  showCustomName.value = false
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.addResource(props.service, serviceResource.value!)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $i18n.t('components.service.actions.addResource.success'),
        text: $i18n.t('components.service.actions.addResource.successInfo'),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      return true
    }
  } catch (e) {
    console.log(e)

    $notification.toast({
      title: $i18n.t('components.service.actions.addResource.error'),
      text: $i18n.t('components.service.actions.addResource.errorInfo', { title: serviceResource.value?.name }),
    })

    emit('error')
  }
}

defineExpose({
  open
})
</script>
